import React, { Suspense, useState } from 'react';
import { notification, Space } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import type { TablePaginationConfig, TableProps } from 'antd';
import { IOrder, ISearchOrderReq, ISearchOrderRes } from '@/interfaces/order';
import { IDataModal } from '@/interfaces/dataModal';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import {
  getOrderStatusColor,
  getOrderStatusName,
  getOrderTypeColor,
  getOrderTypeName,
} from '@/helpers/orderHelper';
import { twMerge } from 'tailwind-merge';
import MediaViewModal from '@/components/media/media-view-modal';
import { EOrderStatus, EOrderType } from '@/enums/EOrder';
import { EShippingType } from '@/enums/EShippingType';
import { getShippingTypeName } from '@/helpers/shippingType';
import { cargoAction } from '@/utils/googleMapUtils';

interface IProps {
  loading: boolean;
  criteria: ISearchOrderReq;
  order: ISearchOrderRes;
  onEditData: (item: IOrder) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IOrder> | SorterResult<IOrder>[],
  ) => void;
}

const _orderModal: IDataModal<IOrder> = {
  open: false,
};

const CustomerOrderTable = ({
  loading,
  criteria,
  order,
  onTableChange,
}: IProps) => {
  const [fileModal, setFileModal] = useState<IDataModal<IOrder>>(_orderModal);

  const onCargo = async (numberPlate: string, lat?: string, lng?: string) => {
    await cargoAction(numberPlate, lat, lng);
  };

  const columns: TableProps<IOrder>['columns'] = [
    {
      sorter: true,
      title: 'รหัส',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold text-blue-700',
      width: 120,
      align: 'center',
      render: (_, rec) => (
        <Space
          onClick={() =>
            window.open(
              rec.orderType === EOrderType.INTERNAL
                ? `/order/${rec.id}`
                : `/export/${rec.id}`,
              '_blank',
            )
          }
          className={twMerge(
            'text-blue-700 underline cursor-pointer',
            rec?.isDelete && 'text-red-500',
          )}
        >
          {_}
        </Space>
      ),
    },
    {
      title: 'ประเภท',
      dataIndex: 'orderType',
      key: 'orderType',
      className: 'font-medium',
      align: 'center',
      width: 100,
      fixed: 'right',
      render: _ => {
        const name = getOrderTypeName(_);
        const status = getOrderTypeColor(_);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-0.5',
            )}
          >
            {name}
          </div>
        );
      },
    },
    {
      sorter: true,
      title: 'วันที่',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 100,
      align: 'center',
      className: 'font-medium',
      render: _ => formatDate(_),
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
    },
    {
      title: 'ขนส่ง',
      dataIndex: 'numberPlate',
      key: 'numberPlate',
      width: 100,
      align: 'center',
      render: (_, rec) => {
        const isDispatch =
          rec.status == EOrderStatus.COMPLETED &&
          rec.transportation === EShippingType.TRUCK;
        const tabians = rec.orderDrivers
          ?.filter(c => c.isActive)
          ?.map(c => c.tabian);
        return (
          <div
            className={twMerge(
              isDispatch &&
                'underline text-blue-700 cursor-pointer underline-offset-2',
            )}
            onClick={() =>
              isDispatch
                ? (rec?.keminTruckOrders?.licencePlateNumber &&
                    onCargo(
                      rec?.keminTruckOrders?.licencePlateNumber,
                      rec?.shippingAddress?.lat,
                      rec?.shippingAddress?.lng,
                    )) ||
                  notification.info({ message: 'ไม่พบข้อมูล' })
                : notification.info({ message: 'ไม่พบข้อมูล' })
            }
          >
            {rec.transportation === EShippingType.PUIS
              ? getShippingTypeName(rec.transportation)
              : (isDispatch && rec?.keminTruckOrders?.licencePlateNumber) ||
                tabians.join(', ')}
          </div>
        );
      },
    },
    {
      title: 'ที่จัดส่ง',
      dataIndex: 'shippingAddress',
      key: 'shippingAddress',
      width: 200,
      render: (_, rec) => (
        <div className="truncate">{rec?.shippingAddress?.name}</div>
      ),
    },
    {
      title: 'สินค้า',
      dataIndex: 'orderItems',
      key: 'orderItems',
      render: (_, rec) => (
        <div className="truncate overflow-hidden text-xs">
          {rec?.orderItems
            ?.map(i => {
              return `(${i.productName}=${formatNumber(Number(i.quantity))})`;
            })
            .join(', ')}
        </div>
      ),
    },
    {
      sorter: true,
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      className: 'font-medium',
      align: 'center',
      width: 100,
      fixed: 'right',
      render: (_, rec) => {
        const name = getOrderStatusName(
          _,
          rec.transportation,
          false,
          rec.interfaceErrorCode,
        );
        const status = getOrderStatusColor(_, rec.transportation);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-0.5',
            )}
          >
            {name}
          </div>
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 40,
      render: (_, rec) => (
        <div className="flex flex-row divide-x divide-dashed items-center w-full justify-center">
          <FileSearchOutlined
            className="text-blue-700 px-1.5"
            onClick={() =>
              setFileModal({
                open: true,
                item: rec,
                id: rec.id,
              })
            }
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IOrder>
        columns={columns}
        criteria={criteria}
        data={order}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1600}
        scroll
      />
      <Suspense>
        <MediaViewModal
          modelId={Number(fileModal?.id) || null}
          modelType={'order'}
          open={fileModal?.open}
          onCancel={function (): void {
            setFileModal({
              ..._orderModal,
              open: false,
            });
          }}
        />
      </Suspense>
    </div>
  );
};

export default CustomerOrderTable;
